import Papa from 'papaparse';
import { emailRegex, phoneRegex, validateEmail, normalizePhone, normalizeEmail } from './contactUtils';
import { datasetTagFromFilename, mergeCanonicalTags } from './tagUtils';

const structuredBlockRegex = /(?:^|\n)(?:Body:\s*)?First Name:\s*(.+?)\r?\nLast Name:\s*(.+?)\r?\nPhone Number:\s*(.+?)\r?\nEmail Address:\s*(.+?)(?:\r?\n\r?\n|$)/gi;

const PLACEHOLDER_NAMES = ['the tradeit team', 'tradeit team'];

const normalizeNameValue = (value = '') => value.replace(/\s+/g, ' ').trim().toLowerCase();

const capitalize = (value = '') => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const deriveNameFromEmail = (email = '') => {
  if (!email || typeof email !== 'string') {
    return { firstName: '', lastName: '' };
  }
  const localPart = email.split('@')[0] || '';
  if (!localPart) {
    return { firstName: '', lastName: '' };
  }
  const tokens = localPart
    .replace(/[_+.]/g, ' ')
    .split(/[^a-zA-Z]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (!tokens.length) {
    return { firstName: '', lastName: '' };
  }

  if (tokens.length === 1) {
    return {
      firstName: capitalize(tokens[0]),
      lastName: ''
    };
  }

  return {
    firstName: capitalize(tokens[0]),
    lastName: tokens.slice(1).map(capitalize).join(' ')
  };
};

const cleanNameValue = (value = '') => (value ? value.toString().replace(/\s+/g, ' ').replace(/^"|"$/g, '').trim() : '');

const toTitleCase = (value = '') =>
  cleanNameValue(value)
    .split(/\s+/)
    .filter(Boolean)
    .map(capitalize)
    .join(' ');

const splitFullName = (raw = '') => {
  const cleaned = cleanNameValue(raw);
  if (!cleaned) return { firstName: '', lastName: '' };

  const commaParts = cleaned.split(',');
  if (commaParts.length === 2) {
    const left = cleanNameValue(commaParts[0]);
    const right = cleanNameValue(commaParts[1]);
    const rightParts = right.split(/\s+/).filter(Boolean);
    const firstName = rightParts[0] || '';
    const lastName = rightParts.length > 1 ? rightParts.slice(1).join(' ') : left;
    return {
      firstName,
      lastName: lastName || ''
    };
  }

  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
};

const FIRST_NAME_KEYS = ['firstname', 'first_name', 'first', 'givenname', 'given_name', 'given', 'fname'];
const LAST_NAME_KEYS = ['lastname', 'last_name', 'last', 'surname', 'familyname', 'family_name', 'family', 'lname'];
const FULL_NAME_KEYS = ['fullname', 'full_name', 'full', 'name', 'contact', 'contactname', 'customer', 'client', 'lead', 'person'];
const EXCLUDED_NAME_KEYS = ['company', 'business', 'organization', 'organisation', 'email', 'mail', 'phone', 'number', 'title'];

const extractNamesFromRow = (row = {}, fallbackEmail = '') => {
  let firstName = '';
  let lastName = '';
  let fullNameCandidate = '';

  Object.entries(row).forEach(([key, rawValue]) => {
    if (firstName && lastName) return;
    if (rawValue === null || rawValue === undefined) return;
    const value = cleanNameValue(rawValue);
    if (!value) return;
    const normalizedKey = key ? key.toString().trim().toLowerCase() : '';
    if (!normalizedKey) return;
    const keyNoSpaces = normalizedKey.replace(/\s+/g, '');
    const isExcluded = EXCLUDED_NAME_KEYS.some((token) => normalizedKey.includes(token));
    if (!firstName) {
      const matchesFirst = FIRST_NAME_KEYS.some((token) => keyNoSpaces.includes(token));
      if (matchesFirst) {
        const split = splitFullName(value);
        if (split.firstName) firstName = split.firstName;
        if (!lastName && split.lastName) lastName = split.lastName;
        return;
      }
    }
    if (!lastName) {
      const matchesLast = LAST_NAME_KEYS.some((token) => keyNoSpaces.includes(token));
      if (matchesLast) {
        const split = splitFullName(value);
        if (split.lastName) lastName = split.lastName;
        if (!firstName && split.firstName) firstName = split.firstName;
        return;
      }
    }
    if (!isExcluded && !fullNameCandidate) {
      const matchesFull = FULL_NAME_KEYS.some((token) => keyNoSpaces.includes(token));
      if (matchesFull && !value.includes('@')) {
        fullNameCandidate = value;
      }
    }
  });

  if ((!firstName || !lastName) && fullNameCandidate) {
    const split = splitFullName(fullNameCandidate);
    if (!firstName && split.firstName) firstName = split.firstName;
    if (!lastName && split.lastName) lastName = split.lastName;
  }

  if ((!firstName || !lastName) && fallbackEmail) {
    const derived = deriveNameFromEmail(fallbackEmail);
    if (!firstName && derived.firstName) firstName = derived.firstName;
    if (!lastName && derived.lastName) lastName = derived.lastName;
  }

  return {
    firstName: toTitleCase(firstName),
    lastName: toTitleCase(lastName)
  };
};

const COMPANY_KEYS = ['company', 'companyname', 'business', 'businessname', 'organization', 'organisation', 'employer', 'firm'];

const PROFILE_FLAG_ALIASES = {
  tradeitDayRegistered: ['tradeitdayregistered'],
  residencyApplied: ['residencyapplied'],
  admissionsCallBooked: ['admissionscallbooked'],
  residencyEnrolled: ['residencyenrolled'],
  societyMember: ['societymember'],
  trndlineUser: ['trndlineuser']
};

const normalizeHeaderKey = (key) => String(key || '').toLowerCase().replace(/[\s_-]+/g, '');

const parseImportedYesNo = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const text = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'y'].includes(text)) return true;
  if (['0', 'false', 'no', 'n'].includes(text)) return false;
  return undefined;
};

const findRowValue = (row, aliases) => {
  for (const [key, value] of Object.entries(row || {})) {
    if (aliases.includes(normalizeHeaderKey(key))) return value;
  }
  return undefined;
};

export const extractProfileFields = (row = {}) => {
  const profile = {};
  Object.entries(PROFILE_FLAG_ALIASES).forEach(([field, aliases]) => {
    const parsed = parseImportedYesNo(findRowValue(row, aliases));
    if (parsed !== undefined) profile[field] = parsed;
  });
  const registeredAt = findRowValue(row, ['tradeitdayregisteredat']);
  if (registeredAt) profile.tradeitDayRegisteredAt = String(registeredAt).trim();
  const marketingStatus = findRowValue(row, ['emailmarketingstatus']);
  if (marketingStatus) {
    const status = String(marketingStatus).trim().toLowerCase();
    if (['subscribed', 'unsubscribed', 'suppressed'].includes(status)) {
      profile.emailMarketingStatus = status;
    }
  }
  const extraSource = findRowValue(row, ['source', 'leadsource', 'origin']);
  if (extraSource) profile.extraSource = String(extraSource).trim();
  return profile;
};

const mergeImportedProfile = (existing, incoming) => {
  Object.keys(PROFILE_FLAG_ALIASES).forEach((field) => {
    if (incoming[field] === true) existing[field] = true;
    else if (existing[field] === undefined && typeof incoming[field] === 'boolean') existing[field] = incoming[field];
  });
  if (incoming.tradeitDayRegisteredAt && !existing.tradeitDayRegisteredAt) {
    existing.tradeitDayRegisteredAt = incoming.tradeitDayRegisteredAt;
  }
  const rank = { subscribed: 0, unsubscribed: 1, suppressed: 2 };
  if (incoming.emailMarketingStatus && rank[incoming.emailMarketingStatus] > (rank[existing.emailMarketingStatus] || 0)) {
    existing.emailMarketingStatus = incoming.emailMarketingStatus;
  }
  const sources = new Set([...(existing.sources || []), existing.source, ...(incoming.sources || []), incoming.source].filter(Boolean));
  existing.sources = Array.from(sources);
};
const PHONE_KEYS = [
  'phone',
  'phonenumber',
  'phone_number',
  'mobile',
  'mobilephone',
  'mobile_number',
  'cell',
  'cellphone',
  'cellphone_number',
  'telephone',
  'telephone_number',
  'tel',
  'contactnumber',
  'contact_number',
  'sms',
  'smsnumber',
  'sms_number',
  'whatsapp',
  'whatsappnumber',
  'whatsapp_number'
];

const extractCompanyFromRow = (row = {}) => {
  for (const [key, rawValue] of Object.entries(row)) {
    if (rawValue === null || rawValue === undefined) continue;
    const value = cleanNameValue(rawValue);
    if (!value) continue;
    const normalizedKey = key ? key.toString().trim().toLowerCase() : '';
    if (!normalizedKey) continue;
    const keyNoSpaces = normalizedKey.replace(/\s+/g, '');
    if (COMPANY_KEYS.some((token) => keyNoSpaces.includes(token))) {
      return value;
    }
  }
  return '';
};

const digitsOnly = (value = '') => String(value).replace(/\D/g, '');

const isLikelyPhoneValue = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim();
  if (!normalized) return false;
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized) || /^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) return false;
  const digits = digitsOnly(normalized);
  return digits.length >= 10 && digits.length <= 15;
};

export const extractPhoneFromRow = (row = {}) => {
  let fallbackPhone = '';

  for (const [key, rawValue] of Object.entries(row)) {
    if (rawValue === null || rawValue === undefined) continue;
    const value = String(rawValue).trim();
    if (!value) continue;
    const normalizedKey = key ? key.toString().trim().toLowerCase() : '';
    const keyNoSpaces = normalizedKey.replace(/[\s-]+/g, '');

    if (PHONE_KEYS.some((token) => keyNoSpaces.includes(token))) {
      if (isLikelyPhoneValue(value)) {
        return value;
      }
    }

    if (!fallbackPhone && isLikelyPhoneValue(value) && /(phone|mobile|cell|tel|sms|whatsapp)/i.test(normalizedKey)) {
      fallbackPhone = value;
    }
  }

  if (fallbackPhone) return fallbackPhone;

  for (const rawValue of Object.values(row)) {
    if (isLikelyPhoneValue(rawValue)) {
      return String(rawValue).trim();
    }
  }

  return '';
};

const isPlaceholderName = (firstName = '', lastName = '') => {
  const normalizedFirst = normalizeNameValue(firstName);
  const normalizedLast = normalizeNameValue(lastName);
  const normalizedFull = normalizeNameValue(`${firstName} ${lastName}`);
  return (
    PLACEHOLDER_NAMES.includes(normalizedFirst) ||
    PLACEHOLDER_NAMES.includes(normalizedLast) ||
    PLACEHOLDER_NAMES.includes(normalizedFull)
  );
};

const normalizeNames = (partial = {}) => {
  const result = { ...partial };
  const email = result.email || partial.email;

  let firstName = cleanNameValue(result.firstName || '');
  let lastName = cleanNameValue(result.lastName || '');

  if (isPlaceholderName(firstName, lastName)) {
    const derived = deriveNameFromEmail(email);
    if (derived.firstName) {
      firstName = derived.firstName;
    }
    if (derived.lastName) {
      lastName = derived.lastName;
    }
  }

  if (!firstName && !lastName && email) {
    const derived = deriveNameFromEmail(email);
    firstName = derived.firstName || firstName;
    lastName = derived.lastName || lastName;
  } else if (email) {
    const derived = deriveNameFromEmail(email);
    if (!firstName && derived.firstName) firstName = derived.firstName;
    if (!lastName && derived.lastName) lastName = derived.lastName;
  }

  return {
    ...result,
    firstName: toTitleCase(firstName || result.firstName || ''),
    lastName: toTitleCase(lastName || result.lastName || '')
  };
};

const createContactRecord = (partial, fileName) => {
  const source = partial.source || fileName;
  const sources = Array.from(new Set([source, ...(partial.sources || [])].filter(Boolean)));
  const record = {
    id: partial.id || `parsed-${Date.now()}-${Math.random()}`,
    email: partial.email ? partial.email.trim() : null,
    phone: partial.phone ? normalizePhone(partial.phone) : null,
    firstName: partial.firstName || '',
    lastName: partial.lastName || '',
    company: partial.company || '',
    source,
    sources,
    sourceRow: partial.sourceRow,
    snippet: partial.snippet,
    confidence: partial.confidence ?? 0.95,
    validated: partial.validated ?? true,
    status: 'active',
    tags: partial.tags || [],
    createdAt: partial.createdAt || new Date().toISOString()
  };
  Object.keys(PROFILE_FLAG_ALIASES).forEach((field) => {
    if (typeof partial[field] === 'boolean') record[field] = partial[field];
  });
  if (partial.tradeitDayRegisteredAt) record.tradeitDayRegisteredAt = partial.tradeitDayRegisteredAt;
  if (partial.emailMarketingStatus) record.emailMarketingStatus = partial.emailMarketingStatus;
  return record;
};

export const parseFile = async (file, options = {}) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = (e) => {
    const content = e.target.result;
    const contacts = [];
    const emailMap = new Map();
    const phoneMap = new Map();
    const sourceDisplay = options.sourceDisplay || file.name || '';
    const sourceKey = (options.sourceKey || sourceDisplay || '').trim().toLowerCase();
    const datasetTag = datasetTagFromFilename(sourceDisplay);
    const applyDatasetTags = (tags = []) => mergeCanonicalTags(datasetTag ? [datasetTag] : [], tags);

    const addContact = (partial) => {
      const enrichedTags = applyDatasetTags(partial.tags || []);
      const enrichedPartial = normalizeNames({ ...partial, tags: enrichedTags });
      const normalizedEmail = enrichedPartial.email ? normalizeEmail(enrichedPartial.email) : null;
      const normalizedPhone = enrichedPartial.phone ? normalizePhone(enrichedPartial.phone) : null;

      let existing = null;
      if (normalizedEmail && emailMap.has(normalizedEmail)) {
        existing = emailMap.get(normalizedEmail);
      }
      if (!existing && normalizedPhone && phoneMap.has(normalizedPhone)) {
        existing = phoneMap.get(normalizedPhone);
      }

      if (existing) {
        if (!existing.email && normalizedEmail) {
          existing.email = normalizedEmail;
          emailMap.set(normalizedEmail, existing);
        }
        if (!existing.phone && normalizedPhone) {
          existing.phone = normalizedPhone;
          phoneMap.set(normalizedPhone, existing);
        }
        const sanitizedExisting = normalizeNames(existing);
        existing.firstName = sanitizedExisting.firstName;
        existing.lastName = sanitizedExisting.lastName;

        if (
          (!sanitizedExisting.firstName || isPlaceholderName(sanitizedExisting.firstName, sanitizedExisting.lastName)) &&
          enrichedPartial.firstName
        ) {
          existing.firstName = enrichedPartial.firstName;
        }
        if (
          (!sanitizedExisting.lastName || isPlaceholderName(existing.firstName, sanitizedExisting.lastName)) &&
          enrichedPartial.lastName
        ) {
          existing.lastName = enrichedPartial.lastName;
        }
        if (!existing.company && enrichedPartial.company) existing.company = enrichedPartial.company;
        if (!existing.snippet && enrichedPartial.snippet) existing.snippet = enrichedPartial.snippet;
        existing.tags = mergeCanonicalTags(existing.tags || [], enrichedTags);
        mergeImportedProfile(existing, enrichedPartial);
        return;
      }

      if (!normalizedEmail && !normalizedPhone) {
        return;
      }

      const sanitizedEnriched = normalizeNames(enrichedPartial);
      const record = createContactRecord(
        {
          ...sanitizedEnriched,
          email: normalizedEmail || null,
          phone: normalizedPhone || null
        },
        sourceKey || file.name
      );
      contacts.push(record);
      if (normalizedEmail) emailMap.set(normalizedEmail, record);
      if (normalizedPhone) phoneMap.set(normalizedPhone, record);
    };

    const handleCsv = () => {
      Papa.parse(content, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          results.data.forEach((row, idx) => {
            const emailCandidate = row.email || row.Email || row['Email Address'] || row['email_address'];
            const phoneCandidate = extractPhoneFromRow(row);
            const inlineEmail = Object.values(row).find((value) => typeof value === 'string' && emailRegex.test(value)) || '';
            const { firstName, lastName } = extractNamesFromRow(row, emailCandidate || inlineEmail);
            const companyCandidate = extractCompanyFromRow(row) || row.company || row.Company || row.organization || row.Organization || '';
            const { extraSource, ...profile } = extractProfileFields(row);
            const baseContact = {
              id: `csv-${idx}-${Date.now()}-${Math.random()}`,
              email: emailCandidate,
              phone: phoneCandidate,
              firstName: firstName || row.first_name || row.firstName || row['First Name'] || '',
              lastName: lastName || row.last_name || row.lastName || row['Last Name'] || '',
              company: companyCandidate,
              source: file.name,
              sources: [file.name, extraSource].filter(Boolean),
              sourceRow: idx + 1,
              snippet: JSON.stringify(row).substring(0, 200),
              confidence: 0.99,
              ...profile
            };

            const validColumnEmail = emailCandidate && validateEmail(String(emailCandidate).trim());
            if (validColumnEmail) {
              addContact(baseContact);
              return;
            }

            let foundInlineEmail = false;
            Object.values(row).forEach((value) => {
              if (typeof value !== 'string') return;
              const matches = value.match(emailRegex) || [];
              matches.forEach((matchEmail) => {
                if (validateEmail(matchEmail)) {
                  foundInlineEmail = true;
                  addContact({ ...baseContact, email: matchEmail, snippet: value.substring(0, 200) });
                }
              });
            });

            if (!foundInlineEmail && phoneCandidate && isLikelyPhoneValue(phoneCandidate)) {
              addContact({ ...baseContact, email: null });
            }
          });

          resolve(contacts);
        },
        error: (error) => reject(error)
      });
    };

    const handleText = async () => {
      const createStructuredPattern = () => new RegExp(structuredBlockRegex.source, structuredBlockRegex.flags);
      const createEmailPattern = () => new RegExp(emailRegex.source, emailRegex.flags);
      const createPhonePattern = () => new RegExp(phoneRegex.source, phoneRegex.flags);
      let iterationsSinceYield = 0;
      const minDigitsForPhone = 7;

      const maybeYield = async () => {
        iterationsSinceYield += 1;
        if (iterationsSinceYield >= 500) {
          iterationsSinceYield = 0;
          await new Promise((resolveYield) => setTimeout(resolveYield, 0));
        }
      };

      const normalizeSnippet = (text) => text.replace(/\s+/g, ' ').trim().slice(0, 200);

      const digitsOnly = (value) => (value ? value.replace(/\D/g, '') : '');

      const cleanPhoneCandidate = (raw) => {
        if (!raw) return null;
        if (/\d{4}[-/]\d{2}[-/]\d{2}/.test(raw) || /\d{2}[-/]\d{2}[-/]\d{4}/.test(raw)) {
          return null;
        }
        if (/\d{1,2}:\d{2}/.test(raw)) {
          return null;
        }
        const withoutExtension = raw.replace(/(?:ext|x|extension)\s*\d+$/i, '').trim();
        const cleaned = withoutExtension.replace(/[^\d+().\s/-]/g, ' ').replace(/\s+/g, ' ').trim();
        const digits = digitsOnly(cleaned);
        if (digits.length < minDigitsForPhone) return null;
        return cleaned;
      };

      const extractPhoneFromContext = (text) => {
        if (!text) return null;
        const labeledPattern = /(?:phone|mobile|cell|tel|telephone|contact)\s*(?:[:=-]|is)?\s*([+()\d][+\d()\s./-]{5,})/gi;
        let match = labeledPattern.exec(text);
        while (match) {
          const candidate = cleanPhoneCandidate(match[1]);
          if (candidate) return candidate;
          match = labeledPattern.exec(text);
        }

        const telLinkPattern = /tel:\s*([+()\d][+\d()\s./-]{5,})/gi;
        match = telLinkPattern.exec(text);
        while (match) {
          const candidate = cleanPhoneCandidate(match[1]);
          if (candidate) return candidate;
          match = telLinkPattern.exec(text);
        }

        return null;
      };

      const processStructuredBlocks = async (text, meta = {}) => {
        const structuredPattern = createStructuredPattern();
        let structuredMatch;
        while ((structuredMatch = structuredPattern.exec(text)) !== null) {
          const email = structuredMatch[4]?.trim();
          if (email && validateEmail(email)) {
            const phone = structuredMatch[3]?.trim() || null;
            const firstName = structuredMatch[1]?.trim() || meta.fallbackFirstName || '';
            const lastName = structuredMatch[2]?.trim() || meta.fallbackLastName || '';
              addContact({
                id: `structured-${contacts.length}-${Date.now()}-${Math.random()}`,
                email,
                phone,
              firstName,
              lastName,
                source: file.name,
                sourceRow: meta.sourceRow,
                snippet: normalizeSnippet(structuredMatch[0]),
              confidence: phone ? 0.99 : 0.85,
                tags: meta.tags
              });
          }
          // eslint-disable-next-line no-await-in-loop
          await maybeYield();
        }
      };

      const processKeyValueBlocks = async (text, meta = {}) => {
        const lines = text.split(/\r?\n/);
        let candidate = {
          firstName: '',
          lastName: '',
          email: null,
          phone: null,
          company: ''
        };
        let snippetLines = [];

        const resetCandidate = () => {
          candidate = {
            firstName: '',
            lastName: '',
            email: null,
            phone: null,
            company: ''
          };
          snippetLines = [];
        };

        const flushCandidate = async () => {
          if (candidate.email && validateEmail(candidate.email)) {
            const firstName = candidate.firstName || meta.fallbackFirstName || '';
            const lastName = candidate.lastName || meta.fallbackLastName || '';
            addContact({
              id: `kv-${contacts.length}-${Date.now()}-${Math.random()}`,
              email: candidate.email,
              phone: candidate.phone,
              firstName,
              lastName,
              company: candidate.company || '',
              source: file.name,
              sourceRow: meta.sourceRow,
              snippet: normalizeSnippet(snippetLines.join(' ')),
              confidence: candidate.phone ? 0.93 : 0.8,
              tags: meta.tags
            });
          }
          resetCandidate();
          // eslint-disable-next-line no-await-in-loop
          await maybeYield();
        };

        for (let i = 0; i < lines.length; i += 1) {
          const rawLine = lines[i];
          const trimmed = rawLine.trim();
          if (!trimmed) {
            if (candidate.email || candidate.phone) {
              // eslint-disable-next-line no-await-in-loop
              await flushCandidate();
            }
            continue;
          }

          const keyMatch = trimmed.match(/^(First Name|Last Name|Full Name|Name|Email|Email Address|Phone|Phone Number|Mobile|Company)[:-]\s*(.+)$/i);
          if (keyMatch) {
            const key = keyMatch[1].toLowerCase();
            const value = keyMatch[2].trim();
            snippetLines.push(trimmed);

            if (key === 'first name') {
              candidate.firstName = value;
            } else if (key === 'last name') {
              candidate.lastName = value;
            } else if (key === 'full name' || key === 'name') {
              const { firstName, lastName } = splitFullName(value);
              candidate.firstName = firstName;
              candidate.lastName = lastName;
            } else if (key === 'email' || key === 'email address') {
              candidate.email = value;
            } else if (key === 'phone' || key === 'phone number' || key === 'mobile') {
              candidate.phone = value;
            } else if (key === 'company') {
              candidate.company = value;
            }

            const ready = candidate.email && validateEmail(candidate.email) && candidate.phone;
            if (ready && i < lines.length - 1) {
              const nextLine = lines[i + 1].trim();
              if (nextLine && !/^(First Name|Last Name|Full Name|Name|Email|Email Address|Phone|Phone Number|Mobile|Company)[:-]/i.test(nextLine)) {
                snippetLines.push(nextLine);
              }
            }
          } else if (candidate.email || candidate.phone) {
            snippetLines.push(trimmed);
          }

          const shouldFlush = candidate.email && validateEmail(candidate.email);
          if (shouldFlush && (i === lines.length - 1 || lines[i + 1].trim() === '' || /^(First Name|Last Name|Full Name|Name|Email|Email Address|Phone|Phone Number|Mobile|Company)[:-]/i.test(lines[i + 1].trim()))) {
            // eslint-disable-next-line no-await-in-loop
            await flushCandidate();
          }

          // eslint-disable-next-line no-await-in-loop
          await maybeYield();
        }

        if (candidate.email || candidate.phone) {
          // eslint-disable-next-line no-await-in-loop
          await flushCandidate();
        }
      };

      const pairEmailPhoneWithinText = async (text, meta = {}) => {
        const emailPattern = createEmailPattern();
        const phonePattern = createPhonePattern();
        const emails = [];
        const phones = [];

        let match;
        while ((match = emailPattern.exec(text)) !== null) {
          const email = match[0];
          if (validateEmail(email)) {
            emails.push({ value: email, index: match.index });
          }
          // eslint-disable-next-line no-await-in-loop
          await maybeYield();
        }

        while ((match = phonePattern.exec(text)) !== null) {
          const cleaned = cleanPhoneCandidate(match[0]);
          if (!cleaned) {
            // eslint-disable-next-line no-await-in-loop
            await maybeYield();
            continue;
          }
          phones.push({ value: cleaned, index: match.index, digits: digitsOnly(cleaned) });
          // eslint-disable-next-line no-await-in-loop
          await maybeYield();
        }

        if (!emails.length) {
          return;
        }

        const usedPhones = new Set();
        const seenEmailsLocal = new Set();

        const findNearestPhone = (position) => {
          let best = null;
          let bestDistance = Infinity;
          phones.forEach((phone) => {
            if (!phone.digits || phone.digits.length < minDigitsForPhone) return;
            if (usedPhones.has(phone.digits)) return;
            const distance = Math.abs(phone.index - position);
            if (distance < bestDistance) {
              best = phone;
              bestDistance = distance;
            }
          });
          return best && bestDistance <= 400 ? best : null;
        };

        for (let i = 0; i < emails.length; i += 1) {
          const email = emails[i];
          if (seenEmailsLocal.has(email.value)) {
            // eslint-disable-next-line no-await-in-loop
            await maybeYield();
            continue;
          }

          const nearestPhone = findNearestPhone(email.index);
          const defaultSnippetStart = nearestPhone ? Math.min(email.index, nearestPhone.index) : email.index;
          const snippetStart = Math.max(0, defaultSnippetStart - 120);
          const snippetEnd = Math.min(text.length, Math.max(email.index + email.value.length, nearestPhone ? nearestPhone.index + nearestPhone.value.length : email.index + email.value.length) + 160);
          const snippet = text.substring(snippetStart, snippetEnd);
          const nameMatch = snippet.match(/(?:First Name|Full Name|Name|From):\s*([A-Za-z]+)(?:\s+([A-Za-z]+))?/i);
          const firstName = (nameMatch ? nameMatch[1] : meta.fallbackFirstName) || '';
          const lastName = (nameMatch && nameMatch[2] ? nameMatch[2] : meta.fallbackLastName) || '';

          let phoneValue = extractPhoneFromContext(snippet);
          if (!phoneValue && nearestPhone) {
            phoneValue = nearestPhone.value;
          }

          if (!phoneValue) {
            // eslint-disable-next-line no-await-in-loop
            await maybeYield();
            continue;
          }

          addContact({
            id: `paired-${contacts.length}-${Date.now()}-${Math.random()}`,
            email: email.value,
            phone: phoneValue,
            firstName,
            lastName,
            source: file.name,
            sourceRow: meta.sourceRow,
            snippet: normalizeSnippet(snippet),
            confidence: phoneValue ? 0.88 : 0.75,
            tags: meta.tags
          });
          if (phoneValue) {
            const digits = digitsOnly(phoneValue);
            if (digits.length >= minDigitsForPhone) {
              usedPhones.add(digits);
              if (nearestPhone && nearestPhone.digits) {
                usedPhones.add(nearestPhone.digits);
              }
            }
          }
          seenEmailsLocal.add(email.value);
          // eslint-disable-next-line no-await-in-loop
          await maybeYield();
        }
      };

      const splitMboxMessages = () => {
        const lines = content.split(/\r?\n/);
        const messages = [];
        let current = [];

        lines.forEach((line, idx) => {
          if (line.startsWith('From ') && current.length) {
            messages.push(current.join('\n'));
            current = [];
          }
          current.push(line);
          if (idx === lines.length - 1 && current.length) {
            messages.push(current.join('\n'));
          }
        });

        return messages;
      };

      const parseHeaders = (rawHeaders) => {
        const headers = {};
        const headerLines = rawHeaders.split(/\r?\n/);
        let currentKey = null;

        headerLines.forEach((line) => {
          if (/^\s/.test(line) && currentKey) {
            headers[currentKey] += ` ${line.trim()}`;
            return;
          }

          const separatorIndex = line.indexOf(':');
          if (separatorIndex === -1) return;
          const key = line.slice(0, separatorIndex).toLowerCase();
          const value = line.slice(separatorIndex + 1).trim();
          headers[key] = value;
          currentKey = key;
        });

        return headers;
      };

      const parseAddressHeader = (value) => {
        if (!value) {
          return { name: '', email: '' };
        }
        const emailMatch = value.match(/<([^>]+)>/);
        const email = emailMatch ? emailMatch[1].trim() : value.trim();
        const namePart = emailMatch ? value.replace(emailMatch[0], '').trim() : '';
        const { firstName, lastName } = splitFullName(namePart);
        const normalized = normalizeNames({ email, firstName, lastName });
        return { name: namePart, email, firstName: normalized.firstName, lastName: normalized.lastName };
      };

      const handleMbox = async () => {
        const messages = splitMboxMessages();
        for (let idx = 0; idx < messages.length; idx += 1) {
          const rawMessage = messages[idx];
          if (!rawMessage.trim()) {
            // eslint-disable-next-line no-await-in-loop
            await maybeYield();
            continue;
          }

          const segments = rawMessage.split(/\r?\n\r?\n/);
          if (segments.length < 2) {
            // eslint-disable-next-line no-await-in-loop
            await maybeYield();
            continue;
          }

          const rawHeaders = segments.shift();
          const body = segments.join('\n\n');
          if (!body.trim()) {
            // eslint-disable-next-line no-await-in-loop
            await maybeYield();
            continue;
          }

          const headers = parseHeaders(rawHeaders);
          const labels = headers['x-gmail-labels'];
          if (labels) {
            const labelList = labels.split(',').map((label) => label.trim().toLowerCase());
            const skipLabels = ['spam', 'trash', 'deleted items'];
            const shouldSkip = labelList.some((label) => skipLabels.includes(label));
            if (shouldSkip) {
              // eslint-disable-next-line no-await-in-loop
              await maybeYield();
              continue;
            }
          }

          const fromInfo = parseAddressHeader(headers.from);

          const meta = {
            sourceRow: idx + 1,
            tags: applyDatasetTags(),
            fallbackFirstName: fromInfo.firstName,
            fallbackLastName: fromInfo.lastName
          };

          await processStructuredBlocks(body, meta);
          await processKeyValueBlocks(body, meta);
          await pairEmailPhoneWithinText(body, meta);

          if (fromInfo.email && validateEmail(fromInfo.email)) {
            const normalizedEmail = normalizeEmail(fromInfo.email);
            if (normalizedEmail && !emailMap.has(normalizedEmail)) {
              addContact({
                id: `header-${contacts.length}-${Date.now()}-${Math.random()}`,
                email: fromInfo.email,
                firstName: fromInfo.firstName || meta.fallbackFirstName || '',
                lastName: fromInfo.lastName || meta.fallbackLastName || '',
                phone: null,
                source: file.name,
                sourceRow: meta.sourceRow,
                snippet: normalizeSnippet(body.slice(0, 200)),
                confidence: 0.72,
                tags: meta.tags
              });
            }
          }

          // eslint-disable-next-line no-await-in-loop
          await maybeYield();
        }
      };

      const handlePlainText = async () => {
        const meta = { sourceRow: 0, tags: applyDatasetTags() };
        await processStructuredBlocks(content, meta);
        await processKeyValueBlocks(content, meta);
        await pairEmailPhoneWithinText(content, meta);
      };

      if (file.name.endsWith('.mbox')) {
        await handleMbox();
        return contacts;
      }

      await handlePlainText();
      return contacts;
    };

    if (file.name.endsWith('.csv')) {
      handleCsv();
      return;
    }

    if (file.name.endsWith('.mbox') || file.name.endsWith('.txt')) {
      handleText().then(resolve).catch(reject);
      return;
    }

    resolve(contacts);
  };

  reader.onerror = () => reject(new Error('File reading failed'));
  reader.readAsText(file);
});

